import { useEffect, useState } from "react";
import { listProducts, createProduct, updateProduct, deleteProduct, generateProductImage, generateMarketingKit } from "@/lib/api";
import { Plus, Trash, Copy, Package, PencilSimple, X, CurrencyDollar, Link as LinkIcon, Check, MagicWand, CircleNotch, Palette, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import SellviaImporter from "@/components/SellviaImporter";

const emptyForm = {
  name: "", description: "", price: 0, sku: "", image_url: "", sellvia_source_url: "", currency: "usd",
  product_type: "digital", delivery_type: "url",
  delivery_url: "", delivery_license: "", delivery_text: "", delivery_instructions: ""
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [genImgFor, setGenImgFor] = useState(null); // product id currently generating
  const [genKitFor, setGenKitFor] = useState(null); // product id currently generating marketing kit
  const [kitModal, setKitModal] = useState(null); // { product, kit } — displays generated marketing kit
  const [showImporter, setShowImporter] = useState(false);

  const load = async () => {
    const p = await listProducts();
    setProducts(p);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", price: p.price,
      sku: p.sku || "", image_url: p.image_url || "",
      sellvia_source_url: p.sellvia_source_url || "", currency: p.currency || "usd",
      product_type: p.product_type || "digital",
      delivery_type: p.delivery_type || "url",
      delivery_url: p.delivery_url || "",
      delivery_license: p.delivery_license || "",
      delivery_text: p.delivery_text || "",
      delivery_instructions: p.delivery_instructions || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || form.price <= 0) {
      toast.error("Name and price (>0) are required");
      return;
    }
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) {
        await updateProduct(editing.id, payload);
        toast.success("Product updated");
      } else {
        await createProduct(payload);
        toast.success("Product created — payment link ready!");
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this product? Payment links using it will stop working.")) return;
    await deleteProduct(id);
    toast.success("Product deleted");
    load();
  };

  const toggleActive = async (p) => {
    // For digital products, require a delivery method before activating
    if (!p.active && p.product_type === "digital") {
      const hasDelivery = (p.delivery_url && p.delivery_type === "url") ||
                          (p.delivery_license && p.delivery_type === "license") ||
                          (p.delivery_text && p.delivery_type === "text");
      if (!hasDelivery) {
        toast.error("Add a delivery URL / license / text before activating");
        return;
      }
    }
    await updateProduct(p.id, { active: !p.active });
    load();
  };

  const copyLink = (p) => {
    const link = `${window.location.origin}/checkout/${p.id}`;
    navigator.clipboard.writeText(link);
    toast.success("Payment link copied! Share it anywhere.");
  };

  const handleGenImage = async (p) => {
    setGenImgFor(p.id);
    try {
      await generateProductImage(p.id);
      toast.success("AI image generated 🎨");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally {
      setGenImgFor(null);
    }
  };

  const handleGenKit = async (p) => {
    setGenKitFor(p.id);
    try {
      const res = await generateMarketingKit(p.id);
      toast.success(`Marketing kit generated (${res.generated?.length || 0} platforms)`);
      setKitModal({ product: p, kit: res.kit || {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kit generation failed");
    } finally {
      setGenKitFor(null);
    }
  };

  const downloadImage = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="p-8" data-testid="products-page">
      {/* Header */}
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline mb-2">// direct-to-bank checkout</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">
            PRODUCTS &amp; <span className="text-arena-primary">PAYMENT LINKS</span>
          </h1>
          <p className="text-arena-muted mt-2 text-sm">
            Create a product → get a shareable Stripe checkout link. Money goes <span className="text-arena-primary">100% to your Stripe</span>, no Sellvia payment fees. You still fulfill via Sellvia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImporter(true)}
            data-testid="open-sellvia-importer"
            className="border border-arena-primary text-arena-primary px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primary hover:text-black flex items-center gap-2"
          >
            <Storefront size={14} weight="bold" />
            Import from Sellvia
          </button>
          <button
            onClick={openCreate}
            data-testid="new-product-btn"
            className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New Product
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="border border-arena-border bg-arena-surface p-4 mb-4 flex items-start gap-3">
        <CurrencyDollar size={20} weight="bold" className="text-arena-success mt-0.5" />
        <div className="text-xs text-arena-muted">
          <span className="text-arena-success font-bold">HOW IT WORKS:</span> Create a product with your markup price → share the payment link on Reddit/IG/DMs → customer pays via Stripe (money hits <span className="text-arena-primary">YOUR</span> bank) → order auto-appears in <span className="text-arena-primary">Orders</span> queue with shipping info → you click PROCESS to fulfill via Sellvia (their wholesale cost only).
        </div>
      </div>

      {/* Grid */}
      {products.length === 0 ? (
        <div className="border border-arena-border bg-arena-surface p-16 text-center" data-testid="empty-products">
          <Package size={40} weight="thin" className="text-arena-muted mx-auto mb-3" />
          <div className="text-arena-muted text-sm">No products yet. Add your first product to generate a payment link.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map(p => (
            <div key={p.id} className="border border-arena-border bg-arena-surface hover:border-arena-primary transition-colors" data-testid={`product-card-${p.sku || p.id}`}>
              {p.image_url && (
                <div className="aspect-video border-b border-arena-border overflow-hidden bg-black">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-heading text-sm font-bold flex-1 truncate">{p.name}</div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 ${p.active ? 'bg-arena-success text-black' : 'bg-arena-secondary text-arena-muted'}`}>
                      {p.active ? 'live' : 'off'}
                    </span>
                    <span className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${p.product_type === 'physical' ? 'border-blue-400 text-blue-400' : 'border-arena-primary text-arena-primary'}`}>
                      {p.product_type === 'physical' ? 'ship' : 'digital'}
                    </span>
                  </div>
                </div>
                {p.sku && <div className="text-[10px] text-arena-muted mt-1">SKU: {p.sku}</div>}
                <div className="text-2xl font-heading font-bold text-arena-primary mt-2">${p.price.toFixed(2)}</div>
                {p.description && <div className="text-xs text-arena-muted mt-2 line-clamp-2">{p.description}</div>}
                {p.product_type === 'digital' && !p.delivery_url && !p.delivery_license && !p.delivery_text && (
                  <div className="mt-2 text-[10px] text-arena-warning border border-arena-warning px-2 py-1">
                    ⚠ Add delivery URL/license/text before activating
                  </div>
                )}

                <div className="mt-4 flex items-center gap-1">
                  <button
                    onClick={() => copyLink(p)}
                    data-testid={`copy-link-${p.sku || p.id}`}
                    className="flex-1 bg-arena-primary text-black px-3 py-2 text-[10px] tracking-widest uppercase font-bold hover:bg-arena-primaryHover flex items-center justify-center gap-1"
                  >
                    <LinkIcon size={12} weight="bold" />
                    Copy Payment Link
                  </button>
                  <button
                    onClick={() => handleGenKit(p)}
                    disabled={genKitFor === p.id}
                    data-testid={`gen-kit-${p.sku || p.id}`}
                    className="border border-arena-border p-2 hover:border-arena-primary hover:text-arena-primary disabled:opacity-50"
                    title="Generate marketing kit (Pinterest / Instagram / TikTok images)"
                  >
                    {genKitFor === p.id ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : <Palette size={12} weight="bold" />}
                  </button>
                  <button
                    onClick={() => window.open(`/checkout/${p.id}`, "_blank", "noopener")}
                    data-testid={`preview-${p.sku || p.id}`}
                    className="border border-arena-border p-2 hover:border-arena-primary hover:text-arena-primary"
                    title="Preview checkout"
                  >
                    <Copy size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`edit-${p.sku || p.id}`}
                    className="border border-arena-border p-2 hover:border-arena-primary hover:text-arena-primary"
                    title="Edit"
                  >
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => toggleActive(p)}
                    data-testid={`toggle-${p.sku || p.id}`}
                    className="border border-arena-border p-2 hover:border-arena-primary hover:text-arena-primary"
                    title={p.active ? "Deactivate" : "Activate"}
                  >
                    <Check size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => handleDelete(p.id)}
                    data-testid={`delete-product-${p.sku || p.id}`}
                    className="border border-arena-border p-2 hover:border-arena-danger hover:text-arena-danger"
                  >
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div className="bg-arena-surface border border-arena-border w-full max-w-2xl" onClick={e => e.stopPropagation()} data-testid="product-modal">
            <div className="flex items-center justify-between p-5 border-b border-arena-border">
              <h2 className="font-heading text-xl font-bold">{editing ? 'EDIT' : 'NEW'} PRODUCT</h2>
              <button onClick={() => setShowModal(false)} className="text-arena-muted hover:text-arena-danger">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="overline block mb-1">Product Name *</label>
                <input
                  data-testid="prod-input-name"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="overline block mb-1">Description</label>
                <textarea
                  data-testid="prod-input-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none"
                />
              </div>
              <div>
                <label className="overline block mb-1">Price ($) *</label>
                <input
                  data-testid="prod-input-price"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>
              <div>
                <label className="overline block mb-1">SKU</label>
                <input
                  data-testid="prod-input-sku"
                  value={form.sku}
                  onChange={e => setForm({ ...form, sku: e.target.value })}
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="overline block mb-1">Image URL</label>
                <input
                  data-testid="prod-input-image"
                  value={form.image_url}
                  onChange={e => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="overline block mb-1">Sellvia Source URL (optional)</label>
                <input
                  data-testid="prod-input-source"
                  value={form.sellvia_source_url}
                  onChange={e => setForm({ ...form, sellvia_source_url: e.target.value })}
                  placeholder="Link to the Sellvia product page — reminds you which supplier item this maps to"
                  className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                />
              </div>

              {/* Delivery Section */}
              <div className="col-span-2 border-t border-arena-border pt-4 mt-2">
                <div className="overline mb-3 text-arena-primary">// digital delivery</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline block mb-1">Product Type</label>
                    <select
                      data-testid="prod-input-type"
                      value={form.product_type}
                      onChange={e => setForm({ ...form, product_type: e.target.value })}
                      className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                    >
                      <option value="digital">Digital (instant delivery)</option>
                      <option value="physical">Physical (Sellvia fulfillment)</option>
                    </select>
                  </div>
                  {form.product_type === "digital" && (
                    <div>
                      <label className="overline block mb-1">Delivery Method</label>
                      <select
                        data-testid="prod-input-delivery-type"
                        value={form.delivery_type}
                        onChange={e => setForm({ ...form, delivery_type: e.target.value })}
                        className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                      >
                        <option value="url">Download URL (Google Drive, Dropbox, etc.)</option>
                        <option value="license">License Key / Promo Code</option>
                        <option value="text">Inline Text Content</option>
                      </select>
                    </div>
                  )}
                </div>

                {form.product_type === "digital" && form.delivery_type === "url" && (
                  <div className="mt-4">
                    <label className="overline block mb-1">Download URL *</label>
                    <input
                      data-testid="prod-input-delivery-url"
                      value={form.delivery_url}
                      onChange={e => setForm({ ...form, delivery_url: e.target.value })}
                      placeholder="https://drive.google.com/... or https://www.dropbox.com/..."
                      className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
                    />
                    <p className="text-[10px] text-arena-muted mt-1">
                      Tip: In Google Drive, right-click → Share → &quot;Anyone with the link&quot; → Copy link.
                    </p>
                  </div>
                )}

                {form.product_type === "digital" && form.delivery_type === "license" && (
                  <div className="mt-4">
                    <label className="overline block mb-1">License Key / Code *</label>
                    <input
                      data-testid="prod-input-delivery-license"
                      value={form.delivery_license}
                      onChange={e => setForm({ ...form, delivery_license: e.target.value })}
                      placeholder="ABCD-1234-EFGH-5678"
                      className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
                    />
                  </div>
                )}

                {form.product_type === "digital" && form.delivery_type === "text" && (
                  <div className="mt-4">
                    <label className="overline block mb-1">Inline Content *</label>
                    <textarea
                      data-testid="prod-input-delivery-text"
                      value={form.delivery_text}
                      onChange={e => setForm({ ...form, delivery_text: e.target.value })}
                      placeholder="The full content that will be delivered to the customer — recipe, code snippet, template, etc."
                      rows={6}
                      className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none resize-none"
                    />
                  </div>
                )}

                {form.product_type === "digital" && (
                  <div className="mt-4">
                    <label className="overline block mb-1">Delivery Instructions (optional)</label>
                    <input
                      data-testid="prod-input-delivery-instructions"
                      value={form.delivery_instructions}
                      onChange={e => setForm({ ...form, delivery_instructions: e.target.value })}
                      placeholder="e.g. Right-click and Save As to download. Contact support if link expires."
                      className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-arena-border flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} className="border border-arena-border px-4 py-2 text-xs tracking-widest uppercase hover:bg-arena-surfaceHover">Cancel</button>
              <button onClick={handleSave} data-testid="save-product-btn" className="bg-arena-primary text-black px-4 py-2 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover">
                {editing ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Marketing Kit modal */}
      {kitModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setKitModal(null)}>
          <div className="bg-arena-surface border border-arena-border w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="marketing-kit-modal">
            <div className="flex items-center justify-between p-5 border-b border-arena-border sticky top-0 bg-arena-surface z-10">
              <div>
                <div className="overline">// marketing kit</div>
                <h2 className="font-heading text-xl font-bold mt-1">{kitModal.product.name}</h2>
              </div>
              <button onClick={() => setKitModal(null)} className="text-arena-muted hover:text-arena-danger" data-testid="close-kit-modal">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { key: "pinterest", label: "Pinterest 2:3", aspect: "aspect-[2/3]", filename: "pinterest.png" },
                { key: "instagram", label: "Instagram 1:1", aspect: "aspect-square", filename: "instagram.png" },
                { key: "tiktok", label: "TikTok 9:16", aspect: "aspect-[9/16]", filename: "tiktok.png" },
              ].map(({ key, label, aspect, filename }) => (
                <div key={key} className="border border-arena-border bg-arena-bg" data-testid={`kit-${key}`}>
                  <div className="p-3 border-b border-arena-border flex items-center justify-between">
                    <span className="overline text-arena-primary">{label}</span>
                    {kitModal.kit[key] && (
                      <button
                        onClick={() => downloadImage(kitModal.kit[key], `${kitModal.product.sku || 'product'}-${filename}`)}
                        className="text-[10px] tracking-widest uppercase text-arena-primary hover:underline"
                        data-testid={`download-${key}`}
                      >
                        ↓ download
                      </button>
                    )}
                  </div>
                  <div className={`${aspect} bg-black flex items-center justify-center overflow-hidden`}>
                    {kitModal.kit[key] ? (
                      <img src={kitModal.kit[key]} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-arena-muted text-[10px] tracking-widest uppercase p-4 text-center">Not generated</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-arena-border text-[10px] text-arena-muted">
              <span className="overline text-arena-primary">// use these</span> Post the Pinterest one to Pinterest, IG to your feed, TikTok as your video cover. Click download on each to save.
            </div>
          </div>
        </div>
      )}
      {/* Sellvia Importer modal */}
      {showImporter && (
        <SellviaImporter
          onClose={() => setShowImporter(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
